import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { Table, Column, Sort } from "component/shared/table";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	getClientPropertyListItems,
	getClientPropertyFlags,
	getClientPropertiesLoading,
	getClientPropertiesLoaded,
} from "redux/selector";
import {
	fetchClientProperties,
	deleteClientProperty,
} from "redux/slice/client-properties";
import { ClientPropertyListItemResource } from "api/resource/client-property";
import { coldFusionReplatform } from "shared/env";
import * as env from "shared/env";
import * as paths from "routes/paths";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const TITLE = "Supplemental Listings";

// resultsSettings.cfm is not yet replatformed — the settings-notice "click here" links out to the
// existing CF page (spec §"Decisions needed" recommended default).
const RESULTS_SETTINGS_URL = `${env.coldFusionUrl}/idx/admin/resultsSettings.cfm`;

const openDetail = (detailUrl: string) => {
	const url = /^https?:\/\//i.test(detailUrl) ? detailUrl : `${env.coldFusionUrl}${detailUrl}`;
	window.open(url, "_blank");
};

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && user.permissions.supplementalListings}
		title={TITLE}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.Home />
				</FeatherIcon>
				{TITLE}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/cpropseditor.cfm" />}
	/>
);

const ListingsSupplementalBase: React.FC<Props> = ({ user, classes = {}, location, history }) => {
	const dispatch = useDispatch();
	const listings = useSelector((state: RootState) => getClientPropertyListItems(state));
	const flags = useSelector((state: RootState) => getClientPropertyFlags(state));
	const loading = useSelector((state: RootState) => getClientPropertiesLoading(state));
	const loaded = useSelector((state: RootState) => getClientPropertiesLoaded(state));

	const [search, setSearch] = useState("");
	const [sort, setSort] = useState<Sort>({ id: "cityAddress", desc: false });
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		dispatch(fetchClientProperties() as any);
	}, [dispatch]);

	// Success banner from a `message` redirect param (CF cpropseditor_output_1.cfm:34-43 — used after
	// delete/save redirects on the CF side).
	useEffect(() => {
		const message = new URLSearchParams(location.search).get("message");
		if (message) {
			setSnackbar({ open: true, message, severity: "success" });
		}
	}, [location.search]);

	const notify = (message: string, severity: "success" | "error" = "success") =>
		setSnackbar({ open: true, message, severity });

	const openEdit = (id: number) =>
		history.push(Router.generatePath(paths.listingsSupplementalEdit, { id }));

	// Immediate delete, no confirmation dialog (D11 — match CF cpropsdelete.cfm).
	const handleDelete = async (listing: ClientPropertyListItemResource) => {
		try {
			await dispatch(deleteClientProperty({ id: listing.id }) as any).unwrap();
			notify("Supplemental Listings Deleted Successfully");
		} catch{
			notify("Failed to delete listing", "error");
		}
	};

	const columns: Column<ClientPropertyListItemResource>[] = [
		{
			id: "cityAddress",
			title: "Address",
			value: (listing) => {
				// Address-first by design: bold street on line 1, city on line 2. Line 2 street text is
				// GetToken(StreetNumber,1,".") + " " + Left(UCase(StreetName),18). Intentional CP
				// divergence from CF (which bolds City first, cpropseditor_output_1.cfm:98) — do not
				// re-flip to City-first for parity.
				const streetNumberToken = (listing.streetNumber || "").split(".")[0];
				const streetName = (listing.streetName || "").toUpperCase().substring(0, 18);
				const addressLine = `${streetNumberToken} ${streetName}`.trim();
				return (
					<Mui.Link
						component="button"
						type="button"
						onClick={() => openEdit(listing.id)}
						data-testid={`supplemental-edit-${listing.id}`}
						className={classes.cityAddressLink}
					>
						{addressLine && (
							<Mui.Typography variant="body2" className={classes.cityLabel} data-testid={`supplemental-address-${listing.id}`}>
								{addressLine}
							</Mui.Typography>
						)}
						<Mui.Typography variant="body2">
							{(listing.city || "").toUpperCase()}
						</Mui.Typography>
					</Mui.Link>
				);
			},
			sort: (a, b) =>
				(a.city || "").localeCompare(b.city || "") ||
				(a.streetName || "").localeCompare(b.streetName || ""),
		},
		{
			id: "type",
			title: "Type",
			value: (listing) => listing.propertyTypeLabel || listing.propertyType || "",
			sort: (a, b) =>
				(a.propertyTypeLabel || a.propertyType || "").localeCompare(
					b.propertyTypeLabel || b.propertyType || ""
				),
		},
		{
			id: "status",
			title: "Status",
			value: (listing) => listing.status,
			sort: (a, b) => (a.status || "").localeCompare(b.status || ""),
		},
		{
			id: "showOnWebsite",
			title: "Show On Website",
			value: (listing) => (listing.showOnWebsite ? "Yes" : "No"),
			sort: (a, b) => Number(a.showOnWebsite) - Number(b.showOnWebsite),
		},
		{
			id: "showOnAgentPage",
			title: "Show On Agent Page",
			// MAX-4194 deliberate non-change: this column stays gated on showAgentPageColumn,
			// NOT on agentControl. For account rows 7-8 (AGENT_OFFICE but no dropdown) that
			// means it renders a value the user can no longer edit. Gating it on agentControl
			// would be more coherent but changes the list page beyond that ticket's scope.
			hidden: !flags.showAgentPageColumn,
			value: (listing) => (listing.showOnAgentPage ? "Yes" : "No"),
			sort: (a, b) => Number(a.showOnAgentPage) - Number(b.showOnAgentPage),
		},
		{
			id: "action",
			title: "Action",
			align: "right",
			value: (listing) => (
				<>
					<Mui.Tooltip title="View listing on your website">
						<Mui.IconButton
							size="small"
							aria-label="View listing on your website"
							onClick={() => openDetail(listing.detailUrl)}
							data-testid={`supplemental-show-${listing.id}`}
						>
							<FeatherIcon fontSize="small">
								<Icons.ExternalLink />
							</FeatherIcon>
						</Mui.IconButton>
					</Mui.Tooltip>
					<Mui.IconButton
						size="small"
						onClick={() => handleDelete(listing)}
						data-testid={`supplemental-delete-${listing.id}`}
					>
						<FeatherIcon>
							<Icons.Trash />
						</FeatherIcon>
					</Mui.IconButton>
				</>
			),
		},
	];

	// CF's list uses a client-side DataTables search over the rendered rows; match it with a
	// case-insensitive filter across address + type (cpropseditor_output_1.cfm .data-table).
	const filteredListings = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) {
			return listings;
		}
		return listings.filter((l) =>
			[l.streetNumber, l.streetName, l.city, l.propertyTypeLabel, l.status]
				.filter(Boolean)
				.join(" ")
				.toLowerCase()
				.includes(q)
		);
	}, [listings, search]);

	const renderList = () => (
		<Mui.Grid container direction="column" spacing={3}>
			<Mui.Grid item container justifyContent="space-between" alignItems="center" className={classes.headerRow}>
				<Mui.Grid item>
					<Mui.Typography variant="h6">Supplemental Listings ({listings.length})</Mui.Typography>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Button
						variant="contained"
						color="primary"
						onClick={() => history.push(paths.listingsSupplementalNew)}
						data-testid="supplemental-add"
					>
						Add a Supplemental
					</Mui.Button>
				</Mui.Grid>
			</Mui.Grid>

			{/* Settings-notice line (cpropseditor_output_1.cfm:49-59) — read-only; links out to the CF
				results-settings page to change where supplementals display. */}
			<Mui.Grid item>
				<Mui.Typography variant="body2" color="textSecondary">
					{flags.supplementalOnFeaturedYn ? (
						<>
							Your Supplemental Listings are currently set to show on your Featured Properties page. To
							change this setting to show these listings on a separate Supplementals page,{" "}
							<Mui.Link href={RESULTS_SETTINGS_URL}>click here</Mui.Link>.
						</>
					) : (
						<>
							Your Supplemental Listings are currently set to show on a separate Supplementals page. To
							change this setting to show these listings on your Featured Properties page,{" "}
							<Mui.Link href={RESULTS_SETTINGS_URL}>click here</Mui.Link>.
						</>
					)}
				</Mui.Typography>
			</Mui.Grid>

			{listings.length > 0 && (
				<Mui.Grid item>
					<Mui.TextField
						variant="outlined"
						size="small"
						placeholder="Search address or type"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<Mui.InputAdornment position="start">
									<FeatherIcon fontSize="small">
										<Icons.Search />
									</FeatherIcon>
								</Mui.InputAdornment>
							),
						}}
						inputProps={{ "data-testid": "supplemental-search" }}
					/>
				</Mui.Grid>
			)}

			<Mui.Grid item>
				{listings.length === 0 ? (
					<Mui.Typography variant="body2" color="textSecondary">
						No Current Supplemental Listings.
					</Mui.Typography>
				) : filteredListings.length === 0 ? (
					<Mui.Typography variant="body2" color="textSecondary">
						No listings match "{search}".
					</Mui.Typography>
				) : (
					<Table items={filteredListings} columns={columns} sort={sort} onSortChange={setSort} />
				)}
			</Mui.Grid>
		</Mui.Grid>
	);

	return (
		<DashboardLayout
			permitted={user && user.permissions.supplementalListings}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Home />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			<FullPageCard>
				{loading && !loaded ? (
					<Mui.Box display="flex" justifyContent="center" p={4}>
						<Mui.CircularProgress />
					</Mui.Box>
				) : (
					renderList()
				)}
			</FullPageCard>

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity={snackbar.severity}>
					<Mui.Typography>{snackbar.message}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const ListingsSupplemental = withStyles(ListingsSupplementalBase, styles);

const ListingsSupplementalPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <ListingsSupplemental {...props} />;
};

export const ListingsSupplementalPage = withStyles(ListingsSupplementalPageBase, styles);
